import ExcelJS from 'exceljs';
import { normalizePhoneDigits } from './phoneNormalize';

function stripAccents(s) {
  return String(s)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function cellToString(cell) {
  if (!cell || cell.value == null) return '';
  const v = cell.value;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') {
    if (v.text != null) return String(v.text);
    if (v.richText && Array.isArray(v.richText)) {
      return v.richText.map((t) => t.text || '').join('');
    }
    if (v.result != null) return String(v.result);
    if (v.hyperlink) return String(v.hyperlink);
  }
  return String(v);
}

function headerLooksLikePhoneColumn(text) {
  const t = stripAccents(String(text).toLowerCase().trim());
  if (!t) return false;
  return (
    /\btelephone\b/.test(t) ||
    /\btel\b/.test(t) ||
    /\bphone\b/.test(t) ||
    /\bmobile\b/.test(t) ||
    /\bgsm\b/.test(t) ||
    /\bwhatsapp\b/.test(t) ||
    /\bportable\b/.test(t) ||
    /\bnumero\b/.test(t) ||
    /\bn°\b/.test(t) ||
    t === 'n° tel' ||
    t === 'n° téléphone'
  );
}

/**
 * Première ligne : détecte une colonne « téléphone » (FR/EN). Sinon chaque ligne est scannée de gauche à droite.
 * @returns {{ phones: string[], skippedRows: number, sheetName: string }}
 */
export async function parsePhoneNumbersFromXlsxArrayBuffer(buffer) {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const ws = workbook.worksheets[0];
  if (!ws) {
    return { phones: [], skippedRows: 0, sheetName: '' };
  }

  let phoneCol = null;
  const firstRow = ws.getRow(1);
  firstRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (phoneCol != null) return;
    if (headerLooksLikePhoneColumn(cellToString(cell))) phoneCol = colNumber;
  });

  const seen = new Set();
  const phones = [];
  let skippedRows = 0;
  const startDataRow = phoneCol != null ? 2 : 1;

  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber < startDataRow) return;

    let raw = '';
    if (phoneCol != null) {
      raw = cellToString(row.getCell(phoneCol));
    } else {
      row.eachCell({ includeEmpty: false }, (cell) => {
        if (raw) return;
        const s = cellToString(cell).trim();
        const n = normalizePhoneDigits(s);
        if (n.length >= 10) raw = s;
      });
    }

    const trimmed = String(raw).trim();
    if (!trimmed) {
      skippedRows += 1;
      return;
    }
    const norm = normalizePhoneDigits(trimmed);
    if (norm.length < 10) {
      skippedRows += 1;
      return;
    }
    if (seen.has(norm)) return;
    seen.add(norm);
    phones.push(trimmed);
  });

  return { phones, skippedRows, sheetName: ws.name || '' };
}

/**
 * Modèle .xlsx : une colonne téléphone (+ nom optionnel). À remplir depuis Excel / Google Sheets
 * (Canva n’exporte pas les numéros en tableau — copiez vos numéros dans une feuille puis enregistrez en .xlsx).
 */
export async function downloadWhatsappNumbersTemplateXlsx() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Numeros', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = [{ width: 24 }, { width: 36 }];
  ws.addRow(['telephone', 'nom (optionnel — non utilisé pour WhatsApp)']);
  ws.addRow(['+212 611 159 538', 'Exemple']);
  ws.addRow(['0611159538', 'Format national Maroc']);
  ws.addRow(['212611159538', 'Chiffres uniquement']);
  ws.getRow(1).font = { bold: true };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'exemple_numeros_whatsapp.xlsx';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
